/** XeAgents.ai**/
/**
 * Centralized authentication storage module for CodeCanyon compliance.
 * Provides a consistent interface for managing auth tokens and user data.
 * All localStorage access for authentication is routed through this module.
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER: 'user',
  IS_SUPER_ADMIN: 'is_super_admin',
  // ProspectAI session keys
  PROSPECT_AI_SESSIONS: 'smart-prospecting-sessions',
  PROSPECT_AI_CURRENT_SESSION: 'smart-prospecting-current-session',
} as const;

export interface StoredUser {
  id: number;
  email: string;
  name: string;
  isSuperAdmin?: boolean;
  [key: string]: unknown;
}

/**
 * Safely access localStorage with error handling.
 * Returns null if localStorage is not available or an error occurs.
 */
function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`[AuthStorage] Failed to get item "${key}":`, error);
    return null;
  }
}

/**
 * Safely set an item in localStorage with error handling.
 * @returns true if successful, false otherwise.
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`[AuthStorage] Failed to set item "${key}":`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage with error handling.
 * @returns true if successful, false otherwise.
 */
function safeRemoveItem(key: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`[AuthStorage] Failed to remove item "${key}":`, error);
    return false;
  }
}

/**
 * AuthStorage provides a centralized interface for managing authentication tokens
 * and related user data in localStorage.
 */
export const AuthStorage = {
  /**
   * Retrieves the stored authentication token.
   * @returns The auth token if present, or null if not found or on error.
   */
  getToken(): string | null {
    return safeGetItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Stores the authentication token.
   * @param token - The JWT or auth token to store.
   * @returns true if storage was successful, false otherwise.
   */
  setToken(token: string): boolean {
    return safeSetItem(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  /**
   * Removes the stored authentication token.
   * @returns true if removal was successful, false otherwise.
   */
  removeToken(): boolean {
    return safeRemoveItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Checks if a valid authentication token exists.
   * @returns true if a token is present, false otherwise.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && token.length > 0;
  },

  /**
   * Retrieves the stored user data.
   * @returns The parsed user object, or null if not found or on error.
   */
  getUser(): StoredUser | null {
    const userData = safeGetItem(STORAGE_KEYS.USER);
    if (!userData) {
      return null;
    }
    try {
      return JSON.parse(userData) as StoredUser;
    } catch (error) {
      console.error('[AuthStorage] Failed to parse user data:', error);
      return null;
    }
  },

  /**
   * Stores the user data.
   * @param user - The user object to store.
   * @returns true if storage was successful, false otherwise.
   */
  setUser(user: StoredUser): boolean {
    try {
      return safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('[AuthStorage] Failed to stringify user data:', error);
      return false;
    }
  },

  /**
   * Removes the stored user data.
   * @returns true if removal was successful, false otherwise.
   */
  removeUser(): boolean {
    return safeRemoveItem(STORAGE_KEYS.USER);
  },

  /**
   * Checks if the current user is a super admin.
   * @returns true if the user is a super admin, false otherwise.
   */
  isSuperAdmin(): boolean {
    const value = safeGetItem(STORAGE_KEYS.IS_SUPER_ADMIN);
    return value === 'true';
  },

  /**
   * Sets the super admin status.
   * @param isAdmin - Whether the user is a super admin.
   * @returns true if storage was successful, false otherwise.
   */
  setSuperAdmin(isAdmin: boolean): boolean {
    return safeSetItem(STORAGE_KEYS.IS_SUPER_ADMIN, isAdmin ? 'true' : 'false');
  },

  /**
   * Removes the super admin status.
   * @returns true if removal was successful, false otherwise.
   */
  removeSuperAdmin(): boolean {
    return safeRemoveItem(STORAGE_KEYS.IS_SUPER_ADMIN);
  },

  /**
   * Stores all authentication data at once (token, user, admin status).
   * Useful after login/registration.
   * @param token - The auth token.
   * @param user - The user object.
   * @returns true if all storage operations were successful.
   */
  setAuthData(token: string, user: StoredUser): boolean {
    const tokenSet = this.setToken(token);
    const userSet = this.setUser(user);
    const adminSet = this.setSuperAdmin(user.isSuperAdmin ?? false);
    return tokenSet && userSet && adminSet;
  },

  /**
   * Clears all authentication data from storage.
   * Used during logout.
   * @returns true if all removal operations were successful.
   */
  clearAuth(): boolean {
    const tokenRemoved = this.removeToken();
    const userRemoved = this.removeUser();
    const adminRemoved = this.removeSuperAdmin();
    // Clear ProspectAI session data on logout
    safeRemoveItem(STORAGE_KEYS.PROSPECT_AI_SESSIONS);
    safeRemoveItem(STORAGE_KEYS.PROSPECT_AI_CURRENT_SESSION);
    return tokenRemoved && userRemoved && adminRemoved;
  },

  /**
   * Gets the Authorization header value for API requests.
   * @returns The Bearer token header value, or null if no token exists.
   */
  getAuthHeader(): string | null {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  },
};

export default AuthStorage;
